import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraDataTable, SpecraEmptyState, SpecraInlineBanner, SpecraListPage, SpecraPageHeader, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ArrowLeft, RefreshCw } from 'lucide-react';
import { useMemo } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { CmStatusBadge } from '../Deliverables/components/common';
import { cmDate } from '../Deliverables/lib/format';
import { occurrenceStatus } from '../Deliverables/lib/status';
import { CommandCenterFilterBar } from './CommandCenterFilterBar';
import { EMPTY_FILTER, isDefaultFilter, isoDay, parseFilter, summaryBody, writeFilter } from './filters';
import { usePortfolioOccurrences } from './hooks';
import { bandParts, dueWords, GROUP_OPTIONS, listDescription, listTitle, parseGroup, parsePanel, serverGroup } from './listView';
import { PortfolioOccurrencePanel } from './PortfolioOccurrencePanel';
export function DeliverablesListPage() {
    const companyId = useTenantId();
    const [params, setParams] = useSearchParams();
    const location = useLocation();
    const filter = useMemo(()=>parseFilter(params), [
        params
    ]);
    const group = parseGroup(params);
    const panel = parsePanel(params);
    const today = isoDay(new Date());
    const body = useMemo(()=>summaryBody(filter, new Date(`${today}T00:00:00`)), [
        filter,
        today
    ]);
    const list = usePortfolioOccurrences(body, serverGroup(group));
    const pages = list.data?.pages ?? [];
    const first = pages[0];
    const res = first?.body;
    const rows = useMemo(()=>pages.flatMap((p)=>p.body.rows), [
        pages
    ]);
    const asOf = res?.asOf || today;
    const setFilter = (next)=>setParams((prev)=>{
            const out = writeFilter(next, prev);
            out.delete('occ');
            out.delete('in');
            return out;
        });
    const setGroup = (g)=>setParams((prev)=>{
            const out = new URLSearchParams(prev);
            if (g === 'program') out.delete('group');
            else out.set('group', g);
            return out;
        });
    const openPanel = (r)=>setParams((prev)=>{
            const out = new URLSearchParams(prev);
            out.set('occ', r.occurrence?.id ?? '');
            out.set('in', r.programId);
            return out;
        });
    const closePanel = ()=>setParams((prev)=>{
            const out = new URLSearchParams(prev);
            out.delete('occ');
            out.delete('in');
            return out;
        });
    const share = async ()=>{
        try {
            await navigator.clipboard.writeText(`${window.location.origin}${location.pathname}${location.search}`);
            toast.success('Link copied. Anyone with access sees the same list.');
        } catch  {
            toast.error('Could not copy the link. Copy it from the address bar instead.');
        }
    };
    const programs = (first?.programOptions ?? []).map((p)=>({
            id: p.id,
            label: p.name || 'Untitled program'
        }));
    const groups = useMemo(()=>{
        if (group === 'none' || !res) return undefined;
        const byKey = new Map();
        for (const r of rows)byKey.set(r.groupKey, [
            ...byKey.get(r.groupKey) ?? [],
            r
        ]);
        return res.groups.filter((g)=>byKey.has(g.key)).map((g)=>({
                id: g.key || 'none',
                label: /*#__PURE__*/ _jsxs("span", {
                    children: [
                        g.label,
                        g.sublabel ? /*#__PURE__*/ _jsxs("span", {
                            className: "font-normal normal-case tracking-normal text-muted-foreground",
                            children: [
                                ' ',
                                "· ",
                                g.sublabel
                            ]
                        }) : null
                    ]
                }),
                // Only the parts that say something: "13 late", never "13 · 13 late".
                summary: [
                    g.late === g.count ? `${g.count.toLocaleString()} late` : g.atRisk === g.count ? `${g.count.toLocaleString()} at risk` : `${g.count.toLocaleString()}`,
                    g.late > 0 && g.late < g.count ? `${g.late.toLocaleString()} late` : '',
                    g.atRisk > 0 && g.atRisk < g.count ? `${g.atRisk.toLocaleString()} at risk` : ''
                ].filter(Boolean).join(' · '),
                testId: Testids.commandCenter.listGroup,
                rows: byKey.get(g.key) ?? []
            }));
    }, [
        group,
        res,
        rows
    ]);
    const panelRow = panel ? rows.find((r)=>r.occurrence?.id === panel.occ) : undefined;
    const filtered = !isDefaultFilter(filter);
    const backHref = `/delivery/command-center${(()=>{
        const qs = writeFilter({
            ...filter,
            states: [],
            from: '',
            to: ''
        }).toString();
        return qs ? `?${qs}` : '';
    })()}`;
    const band = bandParts(res, group);
    const updating = list.isPlaceholderData && list.isFetching;
    const columns = [
        {
            id: 'deliverable',
            header: 'Deliverable',
            cell: (r)=>/*#__PURE__*/ _jsxs("div", {
                    className: "min-w-0",
                    children: [
                        /*#__PURE__*/ _jsxs(SpecraText, {
                            as: "div",
                            className: "truncate font-medium",
                            size: "body-ui",
                            children: [
                                r.dutyTitle || 'Deliverable',
                                r.occurrence?.label ? /*#__PURE__*/ _jsxs("span", {
                                    className: "font-normal text-muted-foreground",
                                    children: [
                                        " · ",
                                        r.occurrence.label
                                    ]
                                }) : null
                            ]
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "div",
                            className: "truncate",
                            size: "meta",
                            tone: "muted",
                            children: [
                                group === 'program' ? '' : r.programName,
                                group === 'team' ? '' : r.workstreamName || 'Contract-wide'
                            ].filter(Boolean).join(' · ')
                        })
                    ]
                })
        },
        {
            id: 'status',
            header: 'Status',
            cell: (r)=>/*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-center gap-1",
                    children: [
                        r.occurrence ? /*#__PURE__*/ _jsx(CmStatusBadge, {
                            status: occurrenceStatus(r.occurrence, r.finalReviewTier, asOf)
                        }) : null,
                        r.atRisk ? /*#__PURE__*/ _jsx(SpecraText, {
                            size: "meta",
                            tone: "warning",
                            children: "At risk"
                        }) : null
                    ]
                })
        },
        {
            id: 'owner',
            header: 'Owner',
            cell: (r)=>r.ownerContactId === '-' || !r.ownerName ? /*#__PURE__*/ _jsx(SpecraText, {
                    size: "body-ui",
                    tone: "muted",
                    children: "No owner"
                }) : /*#__PURE__*/ _jsx(SpecraText, {
                    size: "body-ui",
                    children: r.ownerName
                })
        },
        {
            id: 'due',
            header: 'Due',
            align: 'right',
            cell: (r)=>{
                const d = dueWords(r.occurrence?.dueOn ?? '', asOf);
                const late = r.occurrence?.status?.state === 'late';
                return /*#__PURE__*/ _jsxs("div", {
                    className: "text-right",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "div",
                            className: late ? 'text-danger' : undefined,
                            size: "body-ui",
                            children: d.date
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "div",
                            size: "meta",
                            tone: late ? 'danger' : 'muted',
                            children: d.rel
                        })
                    ]
                });
            }
        },
        {
            id: 'filed',
            header: 'Filed',
            align: 'right',
            cell: (r)=>{
                const l = r.occurrence?.latestSubmission;
                return l ? /*#__PURE__*/ _jsxs("div", {
                    className: "text-right",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "div",
                            size: "body-ui",
                            children: cmDate(l.submittedOn, 'Day not stated')
                        }),
                        /*#__PURE__*/ _jsx(SpecraText, {
                            as: "div",
                            size: "meta",
                            tone: "muted",
                            children: r.waitingSince ? `waiting since ${cmDate(r.waitingSince)}` : `attempt ${l.attempt}`
                        })
                    ]
                }) : /*#__PURE__*/ _jsx(SpecraText, {
                    size: "body-ui",
                    tone: "muted",
                    children: "Nothing filed"
                });
            }
        }
    ];
    const empty = filtered ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
        action: /*#__PURE__*/ _jsx(SpecraButton, {
            onClick: ()=>setFilter(EMPTY_FILTER),
            size: "sm",
            variant: "secondary",
            children: "Clear filters"
        }),
        "data-testid": Testids.commandCenter.listEmpty,
        description: "No deliverable in the programs you can see matches these filters. Clear them, or widen the period to every date.",
        title: "Nothing matches these filters"
    }) : /*#__PURE__*/ _jsx(SpecraEmptyState, {
        "data-testid": Testids.commandCenter.listEmpty,
        description: (first?.hiddenCount ?? 0) > 0 ? `${first?.hiddenCount} programs exist, but you don’t have access to them yet. Ask a delivery lead to add you to a program.` : 'There are no deliverables yet. They appear once a contract is set up for delivery.',
        title: "No deliverables to show yet"
    });
    return /*#__PURE__*/ _jsxs(SpecraListPage, {
        "data-testid": Testids.commandCenter.listPage,
        header: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-2",
            children: [
                /*#__PURE__*/ _jsxs(Link, {
                    className: "inline-flex items-center gap-1 text-[length:var(--fs-ui-meta)] text-muted-foreground hover:text-foreground",
                    "data-testid": Testids.commandCenter.listBack,
                    to: backHref,
                    children: [
                        /*#__PURE__*/ _jsx(ArrowLeft, {
                            "aria-hidden": true,
                            className: "size-3.5"
                        }),
                        " Command Center"
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraPageHeader, {
                    description: listDescription(filter),
                    meta: res ? /*#__PURE__*/ _jsxs(SpecraText, {
                        "data-testid": Testids.commandCenter.listCount,
                        size: "meta",
                        tone: "muted",
                        children: [
                            res.total.toLocaleString(),
                            " ",
                            res.total === 1 ? 'deliverable' : 'deliverables',
                            (first?.hiddenCount ?? 0) > 0 ? ` · ${first?.hiddenCount} programs hidden by access` : ''
                        ]
                    }) : null,
                    title: listTitle(filter.states)
                })
            ]
        }),
        toolbar: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-2",
            children: [
                /*#__PURE__*/ _jsx(CommandCenterFilterBar, {
                    companyId: companyId,
                    filter: filter,
                    onChange: setFilter,
                    onShare: share,
                    owners: res?.ownerOptions ?? [],
                    programs: programs,
                    teams: res?.teamOptions ?? []
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-center justify-between gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            "aria-live": "polite",
                            "data-testid": Testids.commandCenter.listBand,
                            size: "body-ui",
                            tone: "secondary",
                            children: list.isLoading ? 'Reading deliverables…' : updating ? 'Updating…' : band.join(' · ')
                        }),
                        /*#__PURE__*/ _jsx(SpecraSelect, {
                            "aria-label": "Group by",
                            className: "w-auto",
                            "data-testid": Testids.commandCenter.listGroupBy,
                            onChange: (e)=>setGroup(e.target.value),
                            size: "sm",
                            value: group,
                            children: GROUP_OPTIONS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                    value: o.value,
                                    children: o.value === 'none' ? o.label : `Group by ${o.label.toLowerCase()}`
                                }, o.value))
                        })
                    ]
                }),
                res?.mineFallback && filter.scope === 'mine' ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    "data-testid": Testids.commandCenter.mineFallback,
                    tone: "info",
                    variant: "framed",
                    children: "You don’t lead, own a team on or hold a role on any program yet, so My programs shows the whole portfolio: every program you can see."
                }) : null,
                list.isError ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    action: /*#__PURE__*/ _jsx(SpecraButton, {
                        iconLeft: /*#__PURE__*/ _jsx(RefreshCw, {}),
                        onClick: ()=>void list.refetch(),
                        size: "sm",
                        variant: "secondary",
                        children: "Retry"
                    }),
                    "data-testid": Testids.commandCenter.errorBanner,
                    tone: "danger",
                    variant: "framed",
                    children: "Couldn’t load the deliverables. This is a load error, not an empty list."
                }) : null
            ]
        }),
        footer: list.hasNextPage ? /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center justify-center gap-2",
            children: [
                /*#__PURE__*/ _jsxs(SpecraText, {
                    size: "meta",
                    tone: "muted",
                    children: [
                        rows.length.toLocaleString(),
                        " of ",
                        (res?.total ?? 0).toLocaleString(),
                        " shown"
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.commandCenter.listMore,
                    isLoading: list.isFetchingNextPage,
                    onClick: ()=>void list.fetchNextPage(),
                    size: "sm",
                    variant: "secondary",
                    children: "Show more"
                })
            ]
        }) : null,
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: updating ? 'opacity-60 transition-opacity' : undefined,
                children: /*#__PURE__*/ _jsx(SpecraDataTable, {
                    columns: columns,
                    empty: empty,
                    getRowActionLabel: (r)=>`Open ${r.dutyTitle || 'deliverable'}`,
                    getRowId: (r)=>r.occurrence?.id ?? '',
                    getRowLabel: (r)=>r.dutyTitle || 'Deliverable',
                    groups: groups,
                    isRowSelected: (r)=>r.occurrence?.id === panel?.occ,
                    loading: list.isLoading,
                    onRowClick: openPanel,
                    rowTestId: ()=>Testids.commandCenter.listRow,
                    rows: groups ? undefined : rows,
                    testId: Testids.commandCenter.listTable
                })
            }),
            panel ? /*#__PURE__*/ _jsx(PortfolioOccurrencePanel, {
                occId: panel.occ,
                onClose: closePanel,
                programId: panel.program,
                programName: programs.find((p)=>p.id === panel.program)?.label,
                row: panelRow,
                today: asOf
            }, panel.occ) : null
        ]
    });
}
